import { Grid, Menu, MenuItem, Stack } from "@mui/material";
import "react-grid-layout/css/styles.css";
import "rsuite/dist/rsuite-no-reset.min.css";
import { useState } from "react";
import "react-resizable/css/styles.css";
import { useTranslation } from "react-i18next";
import { plus } from "utils/textSymbols";

import { ButtonNew, CustomModal, IconButton } from "..";
import { FilterTypes } from "../../enums/components";
import { DividerHorizontal } from "../Divider";
import { DynamicFilterFactory } from "../DynamicFormInput";
import { FilterChip } from "../FilterChip";

export const TabFiltersModal = ({
  activeFilterCategoryName,
  addFilterName,
  filterCategories,
  removeFilterName,
  setActiveFilterCategoryName,
  setConfig,
  setShowFiltersModal,
  showFiltersModal,
}) => {
  const { t } = useTranslation();
  const [showID, setShowID] = useState(undefined);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  return (
    <CustomModal
      height="90%"
      left="unset"
      modalOpen={showFiltersModal}
      right={24}
      setModalOpen={setShowFiltersModal}
      style={{ transform: "none" }}
      top={24}
      width="60rem"
    >
      <h3 className="font-ds-headline-lg">{t("DynamicDashboard.filters.filterCategories")}</h3>
      <Stack className="h-full w-full flex-col items-center justify-between gap-2 py-4">
        <Grid className="w-full" container spacing={4}>
          {filterCategories?.map((filterCategory, index) => {
            const hasDefinedFilters = filterCategory?.filters?.some((filter) => filter?.filterName);
            if (!hasDefinedFilters) return;

            const noOfActiveFilters = filterCategory?.selectedFiltersNames?.length;
            const isActive = filterCategory.categoryName == activeFilterCategoryName;
            return (
              <Grid item key={index} xs={6}>
                <FilterChip
                  isActive={isActive}
                  name={filterCategory.categoryName}
                  noOfActiveFilters={noOfActiveFilters}
                  onClick={() => setActiveFilterCategoryName(filterCategory.categoryName)}
                />
              </Grid>
            );
          })}
        </Grid>
        <DividerHorizontal />
        <Stack className="w-full !flex-1 items-center overflow-y-auto ">
          {filterCategories?.map((filterCategory, index) => {
            if (filterCategory.categoryName === activeFilterCategoryName)
              return (
                <Stack className="h-full w-full gap-2  px-3" flexDirection="column" key={index}>
                  <ButtonNew
                    aria-controls={showID === index && open ? "basic-menu" + index : undefined}
                    aria-expanded={showID === index && open ? "true" : undefined}
                    aria-haspopup="true"
                    className="mb-4  mt-1"
                    id={"basic-button" + index}
                    onClick={(e) => {
                      setShowID(index);
                      handleClick(e);
                    }}
                    size="sm"
                    variant="secondary"
                  >
                    {plus} {t("DynamicDashboard.filters.addFilter")}
                  </ButtonNew>
                  <Menu
                    anchorEl={anchorEl}
                    id={"basic-menu" + index}
                    MenuListProps={{
                      "aria-labelledby": "basic-button" + index,
                      disablePadding: true,
                    }}
                    onClose={handleClose}
                    open={showID === index && open}
                    sx={{
                      ".MuiMenu-paper": {
                        padding: "8px 0",
                      },
                    }}
                  >
                    {filterCategory?.filters?.map((f, index) => {
                      if (filterCategory.selectedFiltersNames?.includes(f?.filterName)) return;
                      if (f)
                        return (
                          <MenuItem
                            className=""
                            key={index}
                            onClick={() => {
                              handleClose();
                              setShowID(undefined);
                              setConfig(f?.filterName, undefined);
                              addFilterName(f?.filterName);
                            }}
                          >
                            <Stack className="w-full justify-between gap-8 font-ds-body-regular-md">
                              <span>{f?.filterDisplayName}</span>
                              <span>+</span>
                            </Stack>
                          </MenuItem>
                        );
                    })}
                  </Menu>
                  <Stack className=" w-full flex-1 flex-col gap-2 overflow-y-auto  px-3 py-3 ">
                    {filterCategory?.selectedFiltersNames?.map((name) => {
                      const filter = filterCategory?.filters?.find((filter) => filter?.filterName === name);
                      let { filterType, ...args } = filter;
                      if (filterType === FilterTypes.NUMERIC_RANGE_PICKER)
                        args = {
                          ...args,
                          props: {
                            ...args.props,
                            changeByTyping: true,
                            key: name + filterCategory.categoryName,
                          },
                        };
                      const filterFactory = new DynamicFilterFactory(filterType, args);
                      const filterInstance = filterFactory.createFilterClassInstance();
                      if (filterInstance) {
                        const FilterComponent = filterInstance.createComponent();
                        return (
                          <Stack className="w-full gap-1" key={"stack" + name + filterCategory.categoryName}>
                            <IconButton
                              buttonProps={{
                                onClick: () => {
                                  removeFilterName(name);
                                },
                                size: "lg",
                                variant: "tertiary",
                              }}
                              iconProps={{
                                color: "var(--color-purpose-status-negative-subtle)",
                                iconName: "Trash",
                                size: "md",
                              }}
                            />
                            {FilterComponent}
                          </Stack>
                        );
                      }
                    })}
                  </Stack>
                </Stack>
              );
          })}
        </Stack>
      </Stack>
    </CustomModal>
  );
};

export const ChartIndividualFiltersModal = ({ filters }) => {
  return (
    <Stack flexDirection="column" gap={2}>
      {filters?.map((filter) => {
        if (!filter.visible()) return;
        const { filterType, ...args } = filter;
        const filterFactory = new DynamicFilterFactory(filterType, args);
        const filterInstance = filterFactory.createFilterClassInstance();
        if (filterInstance) {
          const FilterComponent = filterInstance.createComponent();
          return FilterComponent;
        }
      })}
    </Stack>
  );
};

export const ChartCategoricalIndividualFilters = ({ filters, filtersCategories, removeChartIndividalFilterKey }) => {
  const removeFilterName = (name) => {
    removeChartIndividalFilterKey(name);
  };

  const [showID, setShowID] = useState(undefined);
  const defaultFilterCategoryName = filtersCategories?.[0]?.categoryName;
  const [activeFilterCategoryName, setActiveFilterCategoryName] = useState(defaultFilterCategoryName);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const isFilterSelected = (config, name) => {
    return Object.keys(config ?? {})?.includes(name);
  };

  return (
    <div className="h-[95%]">
      <h3 className="font-ds-headline-lg">Filter Catergories</h3>
      <Stack className="h-full w-full flex-col items-center justify-between gap-2 py-4">
        <Grid className="w-full" container spacing={4}>
          {filtersCategories?.map((filterCategory, index) => {
            const hasDefinedFilters = filterCategory?.filtersNames?.some((name) =>
              filters?.find((filter) => filter?.filterName === name)
            );
            if (!hasDefinedFilters) return;

            const isActive = filterCategory.categoryName == (activeFilterCategoryName ?? defaultFilterCategoryName);
            const noOfActiveFilters = filterCategory?.filtersNames?.filter((name) => {
              const filter = filters?.find((filter) => filter?.filterName === name);
              return isFilterSelected(filter?.config, filter?.filterName);
            })?.length;
            return (
              <Grid item key={index} xs={6}>
                <FilterChip
                  height="4.2rem"
                  isActive={isActive}
                  name={filterCategory.categoryName}
                  noOfActiveFilters={noOfActiveFilters}
                  onClick={() => setActiveFilterCategoryName(filterCategory.categoryName)}
                />
              </Grid>
            );
          })}
        </Grid>
        <DividerHorizontal />
        <Stack className="w-full !flex-1 items-center overflow-y-auto">
          {filtersCategories?.map((filterCategory, index) => {
            if (filterCategory.categoryName === activeFilterCategoryName)
              return (
                <Stack className="h-full w-full gap-2 overflow-y-auto px-3" flexDirection="column" key={index}>
                  <ButtonNew
                    aria-controls={showID === index && open ? "basic-menu" + index : undefined}
                    aria-expanded={showID === index && open ? "true" : undefined}
                    aria-haspopup="true"
                    className="mb-4  mt-1"
                    id={"basic-button" + index}
                    onClick={(e) => {
                      setShowID(index);
                      handleClick(e);
                    }}
                    size="sm"
                    variant="secondary"
                  >
                    + Add filter
                  </ButtonNew>
                  <Menu
                    anchorEl={anchorEl}
                    id={"basic-menu" + index}
                    MenuListProps={{
                      "aria-labelledby": "basic-button" + index,
                      disablePadding: true,
                    }}
                    onClose={handleClose}
                    open={showID === index && open}
                    sx={{
                      ".MuiMenu-paper": {
                        padding: "8px 0",
                      },
                    }}
                  >
                    {filterCategory?.filtersNames?.map((name, index) => {
                      const filter = filters?.find((filter) => filter?.filterName === name);
                      if (!filter?.visible()) return;

                      let { ...args } = filter;
                      if (isFilterSelected(filter?.config, name)) return;
                      if (filter)
                        return (
                          <MenuItem
                            className=""
                            key={index}
                            onClick={() => {
                              handleClose();
                              setShowID(undefined);
                              filter.setConfig(filter?.filterName, null);
                            }}
                          >
                            <Stack className="w-full justify-between gap-8 font-ds-body-regular-md">
                              <span>{args.filterDisplayName}</span>
                              <span>+</span>
                            </Stack>
                          </MenuItem>
                        );
                    })}
                  </Menu>
                  <Stack className=" w-full flex-1 flex-col gap-2 overflow-y-auto  px-3 pb-2 ">
                    {filterCategory?.filtersNames?.map((name, index) => {
                      const filter = filters?.find((filter) => filter?.filterName === name);
                      if (!filter?.visible()) return;

                      let { filterType, ...args } = filter;
                      const filterFactory = new DynamicFilterFactory(filterType, args);
                      const filterInstance = filterFactory.createFilterClassInstance();
                      if (isFilterSelected(filter?.config, name)) {
                        const FilterComponent = filterInstance.createComponent();
                        return (
                          <Stack className="w-full gap-2" key={index}>
                            <IconButton
                              buttonProps={{
                                onClick: () => {
                                  removeFilterName(name);
                                },
                                size: "lg",
                                variant: "tertiary",
                              }}
                              iconProps={{
                                color: "var(--color-purpose-status-negative-subtle)",
                                iconName: "Trash",
                                size: "md",
                              }}
                            />
                            {FilterComponent}
                          </Stack>
                        );
                      }
                    })}
                  </Stack>
                </Stack>
              );
          })}
        </Stack>
      </Stack>
    </div>
  );
};
